import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { getUtm } from "../lib/utm";
import { pushEvent, spdt } from "../lib/analytics";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Send, Loader2, Gift } from "lucide-react";

const INTERESTS = [
  "Learn to fly (beginner)",
  "FAA Part 107 certification",
  "Improve my flying skills",
  "Just exploring",
];

export const LeadCaptureForm = ({ source = "website", heading, subheading }) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [interest, setInterest] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { name, email, interest, source, ...getUtm() });
      pushEvent("lead_submit", { lead_source: source, interest: interest || "unspecified" });
      spdt("lead", { value: 0, currency: "USD" });
      if (typeof window.fbq === "function") {
        window.fbq("track", "Lead", { content_name: "DronAcademy Beta Interest", content_category: source });
      }
      navigate(`/thank-you?name=${encodeURIComponent(name)}&email=${encodeURIComponent(email)}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
      setLoading(false);
    }
  };

  return (
    <div className="p-7 rounded-sm border border-border/70 bg-card" data-testid="lead-form-card">
      <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-accent mb-3">
        <Gift className="h-3.5 w-3.5" /> Free resource
      </span>
      <h3 className="font-display text-2xl font-bold mb-2">{heading || "Get your free FAA Part 107 checklist"}</h3>
      <p className="text-sm text-slate-400 mb-6">{subheading || "Sign up in seconds and get instant free access to your first lesson and the Basics module."}</p>
      <form onSubmit={submit} className="space-y-3" data-testid="lead-form">
        <Input required placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)}
          className="bg-secondary/40 border-border h-11" data-testid="lead-name" />
        <Input required type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
          className="bg-secondary/40 border-border h-11" data-testid="lead-email" />
        <Select value={interest} onValueChange={setInterest}>
          <SelectTrigger className="bg-secondary/40 border-border h-11" data-testid="lead-interest">
            <SelectValue placeholder="What are you interested in?" />
          </SelectTrigger>
          <SelectContent>
            {INTERESTS.map((i) => (
              <SelectItem key={i} value={i} data-testid={`lead-interest-${i}`}>{i}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="submit" disabled={loading} data-testid="lead-submit"
          className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-semibold">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
          Send me the free checklist
        </Button>
      </form>
    </div>
  );
};
